/**
 * StoffBilV (Stoffstrombilanzverordnung) Legal References
 *
 * Key paragraphs from the German Nutrient Flow Balance Ordinance
 * relevant for farms with significant livestock.
 */

import type { LegalReference } from '@hofsync/design-system';

/**
 * StoffBilV § 3 - Bilanzierungspflicht
 */
export const STOFFBILV_3: LegalReference = {
  id: 'stoffbilv-3',
  law: 'StoffBilV',
  lawFullName: 'Stoffstrombilanzverordnung',
  paragraph: '§ 3',
  title: 'Bilanzierungspflicht',
  shortCitation: 'StoffBilV § 3',
  quote:
    '(1) Der Betriebsinhaber hat jährlich spätestens sechs Monate nach Ablauf des ' +
    'Bezugsjahres eine betriebliche Stoffstrombilanz zu erstellen. Die Stoffstrombilanz ' +
    'ist nach den Vorgaben der Anlage 1 zu erstellen.',
  effectiveDate: '10. Dezember 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/stoffbilv/__3.html',
  relevanceExplanation:
    'Betriebe mit mehr als 50 GV oder mehr als 30 ha und Wirtschaftsdüngeraufnahme sind bilanzierungspflichtig.',
};

/**
 * StoffBilV § 6 - Ermittlung und Berechnung
 */
export const STOFFBILV_6: LegalReference = {
  id: 'stoffbilv-6',
  law: 'StoffBilV',
  lawFullName: 'Stoffstrombilanzverordnung',
  paragraph: '§ 6',
  title: 'Ermittlung und Berechnung',
  shortCitation: 'StoffBilV § 6',
  quote:
    '(1) Für die Ermittlung der Nährstoffmengen nach § 4 und § 5 gelten die Vorgaben ' +
    'nach Anlage 2. Die Nährstoffmengen können durch Verwendung von Messwerten ermittelt ' +
    'oder auf der Grundlage von Standardwerten berechnet werden.',
  effectiveDate: '10. Dezember 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/stoffbilv/__6.html',
  relevanceExplanation:
    'Die Berechnung der Stoffstrombilanz folgt festgelegten Berechnungsvorschriften.',
};

/**
 * StoffBilV § 7 - Bewertung des Bilanzwertes
 */
export const STOFFBILV_7: LegalReference = {
  id: 'stoffbilv-7',
  law: 'StoffBilV',
  lawFullName: 'Stoffstrombilanzverordnung',
  paragraph: '§ 7',
  title: 'Bewertung des Bilanzwertes',
  shortCitation: 'StoffBilV § 7',
  quote:
    '(1) Der nach § 6 ermittelte Bilanzwert für Stickstoff darf im Durchschnitt der ' +
    'letzten drei Bezugsjahre den Wert von 175 Kilogramm Stickstoff je Hektar und Jahr ' +
    'nicht überschreiten. (2) Der Bilanzwert für Phosphat darf im Durchschnitt der ' +
    'letzten sechs Bezugsjahre den Wert von 10 Kilogramm Phosphat je Hektar und Jahr ' +
    'nicht überschreiten.',
  effectiveDate: '10. Dezember 2017',
  lastAmended: '1. Mai 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/stoffbilv/__7.html',
  relevanceExplanation:
    'Die Grenzwerte für N- und P-Saldo dürfen nicht überschritten werden.',
};

/**
 * All StoffBilV legal references indexed by ID
 */
export const STOFFBILV_LEGAL_REFS: Record<string, LegalReference> = {
  'stoffbilv-3': STOFFBILV_3,
  'stoffbilv-6': STOFFBILV_6,
  'stoffbilv-7': STOFFBILV_7,
};
